import { Quotes } from "@phosphor-icons/react";

const ABOUT_IMG =
  "https://customer-assets.emergentagent.com/job_slay-aesthetic-boost/artifacts/nb2spi5w_IMG_8014%20%281%29.jpg";

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative px-6 md:px-12 lg:px-20 py-16 lg:py-20 border-b border-white/10"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left - image sticky */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <div className="relative overflow-hidden border border-white/10">
              <img
                src={ABOUT_IMG}
                alt="AJ — founder and coach"
                className="w-full h-[60vh] lg:h-[82vh] object-cover transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-white">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">Founder · Coach</div>
                  <div className="font-display font-black text-3xl uppercase tracking-tighter mt-1">
                    AJ <span className="font-brush text-[#FF1B91] normal-case text-2xl">&ldquo;SlayJ&rdquo;</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#FF1B91] border border-[#FF1B91]/50 bg-black/50 px-2 py-1">NASM CPT</span>
                    <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#FF1B91] border border-[#FF1B91]/50 bg-black/50 px-2 py-1">NFPT CPT</span>
                    <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#FF1B91] border border-[#FF1B91]/50 bg-black/50 px-2 py-1">CNC</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right - copy */}
        <div className="lg:col-span-7 lg:pl-8">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#FF1B91]" />
            <p data-testid="about-eyebrow" className="text-[11px] uppercase tracking-[0.4em] text-[#FF1B91] font-semibold">
              About Sweat With Slay / 02
            </p>
          </div>

          <h2
            data-testid="about-headline"
            className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.9] tracking-tighter mb-4"
          >
            Your new favorite{" "}
            <span className="font-brush font-normal normal-case text-[#FF1B91] inline-block transform -rotate-2">hype coach.</span>
          </h2>

          <div
            data-testid="about-location"
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mb-10 text-[11px] uppercase tracking-[0.3em] font-semibold text-white/55"
          >
            <span><span className="text-[#FF1B91]">●</span> Tampa-based</span>
            <span className="text-white/25">·</span>
            <span>New York roots</span>
            <span className="text-white/25">·</span>
            <span>Coaching <span className="text-[#FF1B91]">everywhere</span></span>
          </div>

          <div className="relative pl-10 mb-10">
            <Quotes weight="fill" className="absolute left-0 top-0 text-[#FF1B91]" size={28} />
            <p className="font-serif-italic text-2xl md:text-3xl leading-snug text-white/95 max-w-2xl">
              &ldquo;Slay is not a finish line. It&apos;s a mindset. Let&apos;s build something that helps you win.&rdquo;
            </p>
          </div>

          <div className="space-y-5 text-white/75 leading-relaxed max-w-2xl">
            <p>
              I&apos;m <span className="text-white font-semibold">AJ</span> — certified personal trainer, nutrition coach, corporate sales professional, DJ, former athlete, and founder of Sweat With Slay. Somewhere along the way, people started calling me <span className="font-brush text-[#FF1B91] text-xl">SlayJ</span>. It started as a nickname from DJing and working in corporate tech sales, but it eventually became more than that.
            </p>
            <p>
              To me, &ldquo;slay&rdquo; is a mindset. It means showing up with intention, going after your goals, and refusing to quit on yourself — whether that&apos;s in the gym, at work, on the dance floor, or just getting through a chaotic week.
            </p>
            <p>
              I grew up playing sports and went on to play <span className="text-white">Division I lacrosse</span> in college. After injuries forced me to step away from competitive athletics, I had to rebuild my relationship with fitness from the ground up. I was no longer training for a season, a coach, or a scoreboard — I was training for real life. Strength, confidence, energy, longevity, and feeling good in my own body.
            </p>
            <p>
              I still work full-time in corporate sales, so I know what it feels like to juggle work, stress, travel, relationships, and a packed calendar while trying to prioritize your health. That&apos;s exactly why Sweat With Slay exists. My coaching is built for <span className="text-white">busy humans with real lives</span>.
            </p>
            <p>
              Whether you&apos;re brand new to fitness, getting back into a routine, building strength, training for a trip or event, navigating a major life transition, or just tired of starting over every Monday — I&apos;ll help you create a plan that actually fits your schedule, your goals, your equipment, and your lifestyle.
            </p>
            <p className="text-white font-medium">
              No cookie-cutter workouts. No unrealistic expectations. Just smart programming, real accountability, honest support, and a coach who is genuinely obsessed with watching people win.
            </p>
          </div>

          {/* Credentials strip */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10 pt-10">
            {[
              { k: "NASM", v: "CPT" },
              { k: "NFPT", v: "Certified" },
              { k: "CNC", v: "Nutrition coach" },
              { k: "BBA", v: "Business admin" },
            ].map((s) => (
              <div key={s.v} data-testid={`about-stat-${s.v.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="font-display font-black text-3xl lg:text-4xl text-white">{s.k}</div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-2">{s.v}</div>
              </div>
            ))}
          </div>

          {/* Full credentials list */}
          <div className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm text-white/75 border-t border-white/10 pt-10">
            <div className="flex items-start gap-3">
              <span className="text-[#FF1B91] mt-1 flex-shrink-0">●</span>
              <span><span className="text-white font-medium">NASM Certified Personal Trainer</span> (CPT)</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FF1B91] mt-1 flex-shrink-0">●</span>
              <span><span className="text-white font-medium">NFPT Certified</span> Personal Trainer</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FF1B91] mt-1 flex-shrink-0">●</span>
              <span><span className="text-white font-medium">Certified Nutrition Coach</span> — NASM</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FF1B91] mt-1 flex-shrink-0">●</span>
              <span><span className="text-white font-medium">CPR / AED certified</span> — Adult, Child &amp; Infant</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FF1B91] mt-1 flex-shrink-0">●</span>
              <span><span className="text-white font-medium">Bachelor&apos;s in Business Administration</span> — Iona College</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FF1B91] mt-1 flex-shrink-0">●</span>
              <span><span className="text-white font-medium">Enterprise tech sales</span> — full-time, ongoing</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FF1B91] mt-1 flex-shrink-0">●</span>
              <span><span className="text-white font-medium">Board member</span> — Tampa Bay Tech</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FF1B91] mt-1 flex-shrink-0">●</span>
              <span><span className="text-white font-medium">Member</span> — Innovators Council</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FF1B91] mt-1 flex-shrink-0">●</span>
              <span><span className="text-white font-medium">HOA Board</span> — community leadership</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FF1B91] mt-1 flex-shrink-0">●</span>
              <span><span className="text-white font-medium">DJ</span> — performing as <span className="font-brush text-[#FF1B91] text-lg">SlayJ</span></span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FF1B91] mt-1 flex-shrink-0">●</span>
              <span>1+ year coaching at a big-box commercial gym</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FF1B91] mt-1 flex-shrink-0">●</span>
              <span>Former <span className="text-white font-medium">Division I lacrosse</span> athlete</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FF1B91] mt-1 flex-shrink-0">●</span>
              <span>Active in pickleball &amp; golf (we love a hobby)</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-[#FF1B91] mt-1 flex-shrink-0">●</span>
              <span><span className="text-white font-medium">Liability insured</span> via Ergo Next</span>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="/resume"
              data-testid="about-resume"
              className="inline-flex items-center gap-2 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] btn-brutal btn-brutal-pink"
            >
              View full résumé →
            </a>
            <a
              href="https://www.linkedin.com/in/alannajohnsonbd/"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="about-linkedin"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-bold text-white/60 hover:text-[#FF1B91] transition-colors px-4 py-3"
            >
              LinkedIn →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
